import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import clsx from "clsx";

// Client-seitiger Ungelesen-Zähler für die Mail-Nav + den Posteingang-Tab (P1.7,
// Slice A). Die Zahl kommt AUSSCHLIESSLICH aus dem schon geladenen Inbox-State in
// Mail.tsx (`inbox.messages.filter(m => !m.is_read).length`) — Mail.tsx pollt die
// Inbox ohnehin alle 120s. KEIN eigener Fetch, KEIN neuer Egress. Der Wert wird
// nach localStorage gespiegelt, damit der Badge auch VOR dem Mail-Mount (z. B. in
// der Sidebar auf anderen Seiten) da ist — dann ggf. leicht stale, was der Brief
// ausdrücklich erlaubt.

const STORAGE_KEY = "sb_mail_unread";

interface Ctx {
  unreadCount: number;
  setUnreadCount: (n: number) => void;
}

const MailUnreadCtx = createContext<Ctx | null>(null);

function getInitial(): number {
  if (typeof localStorage === "undefined") return 0;
  const raw = localStorage.getItem(STORAGE_KEY);
  const n = raw == null ? 0 : Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function MailUnreadProvider({ children }: { children: ReactNode }) {
  const [unreadCount, setUnreadRaw] = useState<number>(getInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(unreadCount));
    } catch {
      /* localStorage nicht verfügbar → Badge bleibt in-memory. */
    }
  }, [unreadCount]);

  const setUnreadCount = useCallback((n: number) => {
    setUnreadRaw(Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0);
  }, []);

  return (
    <MailUnreadCtx.Provider value={{ unreadCount, setUnreadCount }}>
      {children}
    </MailUnreadCtx.Provider>
  );
}

export function useMailUnread(): Ctx {
  const v = useContext(MailUnreadCtx);
  if (!v) throw new Error("useMailUnread outside MailUnreadProvider");
  return v;
}

/** Dezenter Ungelesen-Badge (accent-Pill mit Zahl, Cap „9+"). Bei `count <= 0`
 *  wird NICHTS gerendert (kein DOM-Knoten) — so ist „bei 0 kein Badge" hart. Ein
 *  einheitliches `data-mail-unread`-Attribut markiert alle Badge-Instanzen (Nav
 *  Desktop, Nav-Kachel Mobile, Posteingang-Tab) für den Visual-Audit. */
export function UnreadPill({ count, className }: { count: number; className?: string }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-mail-unread
      aria-label={`${count} ungelesen`}
      className={clsx(
        "inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1",
        "rounded-full bg-accent-500 text-white text-[10px] font-semibold leading-none tabular-nums",
        className
      )}
    >
      {count > 9 ? "9+" : count}
    </span>
  );
}
